/**
 * @file Ordering-owned hybrid edit and notes modal.
 */

import {
    closeForm,
    setModalCloseGuard,
    setModalCloseCallback,
} from "../../shared/forms/modalShell.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import { renderAuditTrail } from "../../shared/details/detailSections.js";
import {
    DETAIL_TITLE_INPUT_CLASSES,
    FIELD_LABEL_CLASSES,
    FIELD_WRAPPER_CLASSES,
    FIXED_TEXTAREA_CLASSES,
    NATIVE_INPUT_CLASSES,
    REQUIRED_MARK_HTML,
    bindNativeFieldBehavior,
    validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    focusFirstRegisteredControlError,
    validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { state } from "../../app/state/state.js";
import {
    detachOrderingNotesModal,
    mountOrderingNotesModal,
} from "./orderingNotes.js";
import { ensureOrderingBucketsReady } from "./orderingListeners.js";
import {
    applyPendingOrderingMutationOverlays,
    queueOrderingItemCreate,
    queueOrderingItemDelete,
    queueOrderingItemSave,
} from "./orderingMutations.js";
import {
    findOrderingItem,
    getOrderingState,
    placeOrderingItem,
    recordAuthoritativeOrderingItems,
} from "./orderingState.js";

let activeModalItemId = null;
let activeModalDirty = false;
let activeModalNoteDirty = false;
let activeOrderingModalOpen = false;
let activeModalLastDirtyFocusTarget = null;
let modalSession = 0;
let notificationOpenRequest = 0;

function createModalSaveCoordinator(itemForm) {
    const counts = {field: 0, note: 0};
    const itemSubmit = itemForm.querySelector("button[type=submit]");
    let noteSubmit = null;
    let noteField = null;
    const sync = () => {
        const busy = counts.field + counts.note > 0;
        if (itemSubmit) {itemSubmit.disabled = busy;}
        if (noteSubmit) {noteSubmit.disabled = busy;}
        if (noteField) {noteField.disabled = busy;}
        itemForm.toggleAttribute("aria-busy", busy);
    };
    return {
        registerNoteSubmit(control, field) {
            noteSubmit = control;
            noteField = field;
            sync();
        },
        tryBegin(kind) {
            if (!Object.hasOwn(counts, kind) || counts.field + counts.note > 0) {
                return null;
            }
            counts[kind] += 1;
            sync();
            let released = false;
            return () => {
                if (released) {return;}
                released = true;
                counts[kind] = Math.max(0, counts[kind] - 1);
                sync();
            };
        },
    };
}

function editableSnapshot(item) {
    return {
        name: item?.name || "",
        due_date: item?.due_date || null,
        details: item?.details || null,
        listId: item?.listId || "",
    };
}

function editableFieldsMatch(left, right) {
    const leftFields = editableSnapshot(left);
    const rightFields = editableSnapshot(right);
    return Object.keys(leftFields).every((key) =>
        leftFields[key] === rightFields[key]);
}

const ORDERING_DELETE_BUTTON_CLASSES = [
    "inline-flex h-9 items-center justify-center rounded-md bg-red-600 px-4",
    "text-sm font-semibold text-white transition-colors hover:bg-red-700",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/60",
    "focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70",
].join(" ");

function mountOrderingDeleteAction(item, options, session) {
    const cancelButton = document.getElementById("cancel-form-btn");
    const footer = cancelButton?.parentElement;
    if (!footer) {return;}
    const deleteButton = document.createElement("button");
    deleteButton.type = "button";
    deleteButton.dataset.modalCustomActions = "true";
    deleteButton.className = `${ORDERING_DELETE_BUTTON_CLASSES} mr-auto`;
    deleteButton.textContent = "Delete item";
    footer.prepend(deleteButton);
    deleteButton.addEventListener("click", () => {
        openConfirmationModal(
            "Delete Ordering item?",
            "Delete this Ordering item and its related notes and notifications? " +
                "This action cannot be undone.",
            async () => {
                try {
                    await queueOrderingItemDelete(
                        item.id,
                        () => options.onRender?.(),
                        item,
                    );
                    if (session === modalSession && activeModalItemId === item.id) {
                        activeModalDirty = false;
                        activeModalNoteDirty = false;
                        setTimeout(() => {
                            if (session !== modalSession ||
                                activeModalItemId !== item.id) {return;}
                            closeForm();
                            restoreOrderingBoardFocus(
                                options,
                                {kind: "add-item"},
                            );
                        }, 0);
                    }
                } catch (error) {
                    setTimeout(() => showSaveFailure(error), 0);
                    throw error;
                }
            },
            true,
            "Delete item",
            null,
            "Deleting...",
            "Cancel",
            {returnFocusTarget: deleteButton},
        );
    });
}

function fieldMarkup(item) {
    return `
        <div class="pb-16 sm:pb-0" data-ordering-modal-fields>
            <div class="space-y-4">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div class="${FIELD_WRAPPER_CLASSES}">
                        <div data-ordering-date-control></div>
                    </div>
                    <div class="${FIELD_WRAPPER_CLASSES}">
                        <label class="${FIELD_LABEL_CLASSES}">List</label>
                        <div data-ordering-bucket-control></div>
                    </div>
                </div>
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <label class="${FIELD_LABEL_CLASSES}" for="ordering-modal-details">Details</label>
                    <textarea id="ordering-modal-details" name="details" maxlength="20000" class="${FIXED_TEXTAREA_CLASSES}">${escapeHTML(item.details || "")}</textarea>
                </div>
            </div>
            ${renderAuditTrail(item)}
            <section class="mt-4 border-t border-gray-200 pt-4" data-ordering-notes-slot></section>
        </div>
    `;
}

function getOrderingDialogFocusTarget({itemForm}) {
    const dialog = itemForm?.closest("#form-modal");
    if (!(dialog instanceof HTMLElement)) {return null;}
    dialog.tabIndex = -1;
    return dialog;
}

function mountOrderingTitleEditor(formTitle, itemName) {
    const nameField = document.createElement("textarea");
    nameField.id = "ordering-modal-name";
    nameField.name = "name";
    nameField.setAttribute("form", "item-form");
    nameField.maxLength = 180;
    nameField.required = true;
    nameField.rows = 1;
    nameField.placeholder = "Item name";
    nameField.setAttribute("aria-label", "Item name");
    nameField.autocomplete = "off";
    nameField.dataset.autoGrow = "true";
    nameField.className = `${DETAIL_TITLE_INPUT_CLASSES} block max-h-28 resize-none overflow-y-auto`;
    nameField.value = itemName || "";

    const accessibleTitle = document.createElement("span");
    accessibleTitle.className = "sr-only";
    const sync = () => {
        accessibleTitle.textContent = nameField.value.trim() || "Ordering item";
        nameField.style.height = "auto";
        nameField.style.height = `${Math.min(112, nameField.scrollHeight)}px`;
    };
    nameField.addEventListener("input", sync);
    formTitle.replaceChildren(nameField, accessibleTitle);
    sync();
    return {nameField, sync};
}

function showSaveFailure(error) {
    openConfirmationModal(
        "Could not save item",
        escapeHTML(error?.message || "The item changes could not be saved."),
        async () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

function validateOrderingItemForm(itemForm) {
    const validationRoot = itemForm.closest("#form-modal") || itemForm;
    const nativeValid = validateNativeFormFields(validationRoot);
    const registeredValidation = validateRegisteredControls(validationRoot);
    if (!nativeValid || !registeredValidation.valid) {
        if (nativeValid) {
            focusFirstRegisteredControlError(registeredValidation);
        }
        return false;
    }
    return true;
}

function restoreOrderingBoardFocus(options, identity) {
    if (typeof options.onFocusRestore === "function") {
        options.onFocusRestore(identity);
        return;
    }
    requestAnimationFrame(() => {
        const selector = identity.kind === "item" ?
            `[data-ordering-item-id="${CSS.escape(identity.itemId)}"] .ordering-card-open` :
            ".ordering-add-item";
        document.querySelector(selector)?.focus({preventScroll: true});
    });
}

function dirtyCloseMessage() {
    if (activeModalDirty && activeModalNoteDirty) {
        return "Your item field changes and note draft have not been saved.";
    }
    if (activeModalNoteDirty) {return "Your note draft has not been saved.";}
    return "Your item field changes have not been saved.";
}

/**
 * Routes every normal Ordering-modal close through one dirty-state guard.
 * Security/auth teardown uses the explicit immediate-close option.
 * @param {object} [options] Close behavior.
 * @param {boolean} [options.immediate] Bypass prompts for security teardown.
 * @param {Function} [options.onClose] Continuation after close.
 * @param {Function} [options.onStay] Continuation when the user keeps editing.
 * @returns {boolean} Whether this function owned the close request.
 */
export function requestOrderingModalClose(options = {}) {
    if (!activeOrderingModalOpen) {return false;}
    const dirtyReturnFocusTarget = activeModalLastDirtyFocusTarget;
    const finish = () => {
        activeModalDirty = false;
        activeModalNoteDirty = false;
        activeModalLastDirtyFocusTarget = null;
        closeForm();
        options.onClose?.();
    };
    if (options.immediate || !activeModalItemId ||
        (!activeModalDirty && !activeModalNoteDirty)) {
        finish();
        return true;
    }
    openConfirmationModal(
        "Discard unsaved changes?",
        dirtyCloseMessage(),
        finish,
        false,
        "Discard",
        options.onStay || null,
        "",
        "Keep editing",
        {
            returnFocusTarget: () => dirtyReturnFocusTarget,
        },
    );
    return true;
}

function addFieldMarkup() {
    return `
        <div class="space-y-4" data-ordering-add-fields>
            <div class="${FIELD_WRAPPER_CLASSES}">
                <label class="${FIELD_LABEL_CLASSES}" for="ordering-add-name">Item name ${REQUIRED_MARK_HTML}</label>
                <input id="ordering-add-name" name="name" maxlength="180" required class="${NATIVE_INPUT_CLASSES}">
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <label class="${FIELD_LABEL_CLASSES}">List ${REQUIRED_MARK_HTML}</label>
                    <div data-ordering-add-bucket-control></div>
                </div>
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <div data-ordering-add-date-control></div>
                </div>
            </div>
            <div class="${FIELD_WRAPPER_CLASSES}">
                <label class="${FIELD_LABEL_CLASSES}" for="ordering-add-details">Details</label>
                <textarea id="ordering-add-details" name="details" maxlength="20000" rows="2" class="${NATIVE_INPUT_CLASSES} h-14 resize-y"></textarea>
            </div>
        </div>
    `;
}

/**
 * Opens the compact shared-shell Ordering create modal.
 * @param {object} [options] Modal options.
 * @param {Function} [options.onRender] Board reconciliation callback.
 * @returns {void}
 */
export function openOrderingAddModal(options = {}) {
    const buckets = getOrderingState().buckets;
    if (!buckets.length) {return;}
    modalSession += 1;
    const initialDraft = options.initialDraft || {};
    const requestedListId = initialDraft.listId || options.initialListId;
    let listId = buckets.some((bucket) => bucket.id === requestedListId) ?
        requestedListId : "";
    let dueDate = initialDraft.due_date || "";
    activeModalItemId = null;
    activeModalDirty = false;
    activeModalNoteDirty = false;
    activeModalLastDirtyFocusTarget = null;
    activeOrderingModalOpen = true;

    openStandardForm("ordering_item", null, {
        getFieldsHtml: addFieldMarkup,
        afterRender({itemForm}) {
            document.getElementById("form-title").textContent = "Add Ordering item";
            bindNativeFieldBehavior(itemForm);
            const nameInput = itemForm.elements.name;
            const detailsInput = itemForm.elements.details;
            nameInput.value = initialDraft.name || "";
            detailsInput.value = initialDraft.details || "";
            const bucketRoot = itemForm.querySelector("[data-ordering-add-bucket-control]");
            const dateRoot = itemForm.querySelector("[data-ordering-add-date-control]");
            activeModalLastDirtyFocusTarget = nameInput;
            itemForm.addEventListener("focusin", (event) => {
                if (event.target instanceof HTMLElement &&
                    (event.target === nameInput || bucketRoot?.contains(event.target))) {
                    activeModalLastDirtyFocusTarget = event.target;
                }
            });
            createSelectControl({
                root: bucketRoot,
                name: "listId",
                value: listId,
                options: buckets.map((bucket) => ({
                    value: bucket.id,
                    label: bucket.name,
                    order: bucket.order,
                })),
                searchable: buckets.length >= 7,
                portalMenu: true,
                required: true,
                placeholder: "Select list",
                accessibleName: "Ordering list",
                onChange(value) {
                    listId = value;
                    activeModalDirty = true;
                },
            });
            createDateControl({
                root: dateRoot,
                name: "due_date",
                type: "date",
                label: "Needed by",
                value: dueDate,
                onChange(value) {
                    dueDate = value || "";
                    activeModalDirty = true;
                },
            });
            [nameInput, detailsInput].forEach((field) => field.addEventListener("input", () => {
                activeModalDirty = true;
            }));
            setModalCloseCallback(() => {
                modalSession += 1;
                activeModalItemId = null;
                activeModalDirty = false;
                activeModalNoteDirty = false;
                activeModalLastDirtyFocusTarget = null;
                activeOrderingModalOpen = false;
            });

            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                if (!validateOrderingItemForm(itemForm)) {return;}
                const name = nameInput.value.trim();
                const details = detailsInput.value.trim() || null;
                const generation = getOrderingState().routeGeneration;
                const completion = queueOrderingItemCreate({
                    listId,
                    name,
                    due_date: dueDate || null,
                    details,
                    actorId: state.currentUser?.uid || state.currentUserProfile?.id || "",
                }, () => options.onRender?.());
                activeModalDirty = false;
                closeForm();
                restoreOrderingBoardFocus(
                    options,
                    options.focusIdentity || {kind: "add-item"},
                );
                void completion.catch((error) => {
                    if (getOrderingState().routeGeneration !== generation) {return;}
                    showSaveFailure(error);
                });
            };
        },
    });
}

/**
 * Returns the item currently presented by the Ordering modal.
 * @returns {object|null} Current item.
 */
export function getActiveOrderingModalItem() {
    return activeModalItemId ? findOrderingItem(activeModalItemId) : null;
}

/**
 * Opens the hybrid edit/context modal for an Ordering item.
 * @param {object} item Ordering item.
 * @param {object} [options] Modal options.
 * @param {Function} [options.onRender] Board reconciliation callback.
 * @returns {void}
 */
export function openOrderingItemModal(item, options = {}) {
    if (!item?.id) {return;}
    const session = ++modalSession;
    activeModalItemId = item.id;
    activeModalDirty = false;
    activeModalNoteDirty = false;
    activeModalLastDirtyFocusTarget = null;
    activeOrderingModalOpen = true;
    let openedFields = editableSnapshot(item);
    let dueDate = item.due_date || "";
    let listId = item.listId;

    openStandardForm("ordering_item", item, {
        getFieldsHtml: fieldMarkup,
        initialFocus: getOrderingDialogFocusTarget,
        afterRender({itemForm}) {
            const formTitle = document.getElementById("form-title");
            const titleEditor = mountOrderingTitleEditor(formTitle, item.name);
            bindNativeFieldBehavior(formTitle);
            bindNativeFieldBehavior(itemForm);
            const saveCoordinator = createModalSaveCoordinator(itemForm);
            const dateRoot = itemForm.querySelector("[data-ordering-date-control]");
            const bucketRoot = itemForm.querySelector("[data-ordering-bucket-control]");
            const nameField = titleEditor.nameField;
            const detailsField = itemForm.elements.details;
            const currentDraftSnapshot = () => editableSnapshot({
                name: nameField.value.trim(),
                due_date: dueDate || null,
                details: detailsField.value.trim() || null,
                listId,
            });
            const recomputeItemDirty = () => {
                activeModalDirty = !editableFieldsMatch(
                    currentDraftSnapshot(),
                    openedFields,
                );
            };
            activeModalLastDirtyFocusTarget = nameField;
            itemForm.addEventListener("focusin", (event) => {
                if (!(event.target instanceof HTMLElement)) {return;}
                const target = event.target;
                if (target === nameField || target === detailsField ||
                    dateRoot?.contains(target) || bucketRoot?.contains(target) ||
                    target.closest("[data-ordering-notes-slot]")) {
                    activeModalLastDirtyFocusTarget = target;
                }
            });
            createDateControl({
                root: dateRoot,
                name: "due_date",
                type: "date",
                label: "Needed by",
                value: dueDate,
                onChange(value) {
                    dueDate = value || "";
                    recomputeItemDirty();
                },
            });
            createSelectControl({
                root: bucketRoot,
                name: "listId",
                value: listId,
                options: getOrderingState().buckets.map((bucket) => ({
                    value: bucket.id,
                    label: bucket.name,
                    order: bucket.order,
                })),
                searchable: getOrderingState().buckets.length >= 7,
                portalMenu: true,
                required: true,
                accessibleName: "Ordering list",
                onChange(value) {
                    listId = value;
                    recomputeItemDirty();
                },
            });
            itemForm.querySelectorAll("[name=name], textarea[name=details]")
                .forEach((field) => field.addEventListener("input", () => {
                    recomputeItemDirty();
                }));
            nameField.addEventListener("input", recomputeItemDirty);
            setModalCloseGuard(() => requestOrderingModalClose());
            setModalCloseCallback(() => {
                modalSession += 1;
                activeModalItemId = null;
                activeModalDirty = false;
                activeModalNoteDirty = false;
                activeModalLastDirtyFocusTarget = null;
                activeOrderingModalOpen = false;
                detachOrderingNotesModal();
            });
            mountOrderingDeleteAction(item, options, session);
            mountOrderingNotesModal(
                item.id,
                (authoritative) => {
                    if (session !== modalSession || activeModalItemId !== item.id) {return;}
                    recordAuthoritativeOrderingItems([authoritative]);
                    placeOrderingItem(authoritative);
                    applyPendingOrderingMutationOverlays();
                    options.onRender?.();
                },
                saveCoordinator,
                (dirty) => {activeModalNoteDirty = dirty;},
            );

            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const nameInput = itemForm.elements.name;
                const detailsInput = itemForm.elements.details;
                const name = nameInput.value.trim();
                if (!validateOrderingItemForm(itemForm)) {return;}
                const releaseSave = saveCoordinator.tryBegin("field");
                if (!releaseSave) {return;}
                const current = findOrderingItem(item.id) || item;
                if (!editableFieldsMatch(current, openedFields)) {
                    releaseSave();
                    showSaveFailure(new Error(
                        "This item was edited elsewhere while the modal was open. " +
                        "Close and reopen it before saving your changes.",
                    ));
                    return;
                }
                const optimistic = {
                    ...current,
                    name,
                    due_date: dueDate || null,
                    details: detailsInput.value.trim() || null,
                    listId,
                };
                const generation = getOrderingState().routeGeneration;
                const completion = queueOrderingItemSave({
                    itemId: item.id,
                    expectedUpdatedAt: current.updatedAt,
                    name: optimistic.name,
                    due_date: optimistic.due_date,
                    details: optimistic.details,
                    listId: optimistic.listId,
                }, current, optimistic, () => options.onRender?.());
                activeModalDirty = false;
                const closedOptimistically = !activeModalNoteDirty;
                if (closedOptimistically) {
                    closeForm();
                    restoreOrderingBoardFocus(
                        options,
                        {kind: "item", itemId: item.id},
                    );
                }
                try {
                    const response = await completion;
                    if (session !== modalSession || activeModalItemId !== item.id) {return;}
                    const saved = {id: item.id, ...response.item};
                    openedFields = editableSnapshot(saved);
                    nameInput.value = saved.name || "";
                    detailsInput.value = saved.details || "";
                    dueDate = saved.due_date || "";
                    listId = saved.listId;
                    titleEditor.sync();
                    options.onRender?.();
                    if (!activeModalNoteDirty) {
                        closeForm();
                        restoreOrderingBoardFocus(
                            options,
                            {kind: "item", itemId: item.id},
                        );
                    }
                } catch (error) {
                    if (getOrderingState().routeGeneration !== generation) {return;}
                    if (!closedOptimistically && session === modalSession &&
                        activeModalItemId === item.id) {
                        activeModalDirty = true;
                    }
                    showSaveFailure(error);
                } finally {
                    releaseSave();
                }
            };
        },
    });
}

/**
 * Closes an Ordering-owned shared modal during route/auth teardown. The modal
 * close callback owns note detachment and is invoked only once by closeForm.
 * @returns {void}
 */
export function closeOrderingModalForTeardown() {
    requestOrderingModalClose({immediate: true});
}

function showNotificationOutcome(title, message) {
    openConfirmationModal(
        title,
        escapeHTML(message),
        async () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

/**
 * Resolves a notification item and opens the modal only when its bucket is active.
 * @param {string} itemId Ordering item id.
 * @returns {Promise<object>} Resolution outcome.
 */
export async function openOrderingNotificationItem(itemId) {
    const request = ++notificationOpenRequest;
    const isCurrentRequest = () => request === notificationOpenRequest;
    if (!itemId) {
        showNotificationOutcome(
            "Ordering item unavailable",
            "This notification does not contain an Ordering item.",
        );
        return {status: "unavailable"};
    }
    try {
        const generation = getOrderingState().routeGeneration;
        const {doc, getDocFromServer} = await import(
            "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js"
        );
        const {db} = await import("../../app/firebase/firebase.js");
        await ensureOrderingBucketsReady(generation);
        if (!isCurrentRequest() || generation !== getOrderingState().routeGeneration) {
            return {status: "stale"};
        }
        const snapshot = await getDocFromServer(doc(db, "ordering_items", itemId));
        if (!isCurrentRequest() || generation !== getOrderingState().routeGeneration) {
            return {status: "stale"};
        }
        if (!snapshot.exists()) {
            showNotificationOutcome(
                "Ordering item unavailable",
                "The item may have been removed or is no longer available.",
            );
            return {status: "missing"};
        }
        const item = {id: snapshot.id, ...snapshot.data()};
        if (!getOrderingState().buckets
            .some((bucket) => bucket.id === item.listId)) {
            showNotificationOutcome(
                "Ordering item inactive",
                "This item belongs to an archived Ordering list and is not currently available.",
            );
            return {status: "inactive_bucket"};
        }
        recordAuthoritativeOrderingItems([item]);
        placeOrderingItem(item);
        openOrderingItemModal(item, {
            onRender: () => void import("./orderingView.js").then(
                ({renderActiveOrderingView}) => renderActiveOrderingView(),
            ),
        });
        return {status: "opened"};
    } catch (error) {
        if (!isCurrentRequest()) {return {status: "stale"};}
        const denied = error?.code === "permission-denied" ||
            error?.code === "firestore/permission-denied";
        showNotificationOutcome(
            denied ? "Ordering access denied" : "Ordering item unavailable",
            denied ?
                "You no longer have permission to open this Ordering item." :
                "The Ordering item could not be loaded. Try again.",
        );
        return {status: denied ? "denied" : "unavailable"};
    }
}
